import {paperClasses} from '@mui/material/Paper';
import {alpha, darken} from '@mui/material/styles';
import {menuItemClasses} from '@mui/material/MenuItem';
import {listItemIconClasses} from '@mui/material/ListItemIcon';
import {checkboxClasses} from '@mui/material/Checkbox';
import {listClasses} from '@mui/material/List';
import {gridClasses} from '@mui/x-data-grid-premium';
import {tablePaginationClasses} from '@mui/material/TablePagination';

import {gray, green, red, blue, warningOrange} from '../themePrimitives';

export const GridCellPercentColors = {
    0: '#F44336',  // Red
    5: '#F85833',
    10: '#FD6D30',
    15: '#FF812D',
    20: '#FF962A',
    25: '#FFAA28',
    30: '#FFBE25',
    35: '#FFD322',
    40: '#FFE720',
    45: '#FFEB1F',
    50: '#FFEE2D',
    55: '#FFEE43',
    60: '#FFEE58',  // Yellow
    65: '#D5E24E',
    70: '#ACD744',
    75: '#84CB3A',
    80: '#5BC030',
    85: '#33B526',
    90: '#2EA523',
    95: '#2A9520',
    100: '#2E7D32'  // Green
};

function generatePercentCellClassNames(theme) {
    const colorMap = {};

    for (let percent = 0; percent <= 100; percent += 5) {
        colorMap['& .MuiDataGrid-row .percent-cell .MuiDivider-root'] = {
            borderColor: alpha('#000', 0.1)
        };

        colorMap[`& .MuiDataGrid-row .percent-cell-${percent}`] = {
            backgroundColor: GridCellPercentColors[percent],
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row.odd .percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.04),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row.Mui-selected .percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.07),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row.odd.Mui-selected .percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.06),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row:hover .percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.07),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row:hover .percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.06),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };
        colorMap[`& .MuiDataGrid-row .Mui-selected.percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.08),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };

        colorMap[`& .MuiDataGrid-row .Mui-selected.percent-cell-${percent}`] = {
            backgroundColor: darken(GridCellPercentColors[percent], 0.07),
            color: theme.palette.getContrastText(GridCellPercentColors[percent])
        };
    }

    return colorMap;
}

export const dataGridCustomizations = {
    MuiDataGrid: {
        styleOverrides: {
            root: ({theme}) => ({
                ...generatePercentCellClassNames(theme),
                '--DataGrid-overlayHeight': '300px',
                '& .MuiDataGrid-columnSeparator': {
                    color: 'transparent'
                },
                '& .MuiDataGrid-cell:focus': {
                    outline: 'none'
                },
                '& .MuiDataGrid-cell:focus-within': {
                    outline: 'none'
                },
                // Match the cells above: column headers shouldn't draw a focus/
                // active outline when clicked (the default outlines even non-sortable
                // headers, which reads as a stray box).
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                    outline: 'none'
                },
                // The grid rounds its top corners to the grid radius (root
                // border-radius + overflow:clip, plus an explicit radius on the
                // first/last header cells), which shows as a rounded notch on the
                // header — especially on borderless grids. Square the top off
                // (keep the rounded bottom) by zeroing the top radius at every
                // source: the root, the header container, and the header cells.
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                '& .MuiDataGrid-main > *:first-of-type': {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                },
                '& .MuiDataGrid-columnHeader': {
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: 0
                },
                '& .MuiDataGrid-cell--textLeft': {
                    display: 'flex',
                    alignItems: 'center'
                },
                '& .MuiDataGrid-footerCell': {
                    fontWeight: 'bold',
                    color: (theme.vars || theme).palette.text.primary
                },
                '& .MuiDataGrid-cell': {
                    borderRight: '1px solid',
                    borderRightColor: alpha(gray[300], 0.1)
                },
                '& .MuiDataGrid-row--firstVisible .MuiDataGrid-cell': {
                    borderTop: 0
                },
                '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': {
                    borderBottom: 0
                },
                '& .MuiDataGrid-row--detailPanelExpanded': {
                    borderBottom: '1px solid',
                    borderColor: (theme.vars || theme).palette.divider
                },
                '& .MuiDataGrid-pinnedRows--top': {
                    borderBottom: '1px solid',
                    borderColor: (theme.vars || theme).palette.divider
                },
                '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-withBorderColor': {
                    borderRight: '1px solid',
                    borderColor: (theme.vars || theme).palette.divider
                },
                '& .MuiDataGrid-footerContainer.MuiDataGrid-withBorderColor': {
                    borderTop: '1px solid',
                    borderColor: (theme.vars || theme).palette.divider,
                    // The broader `.MuiDataGrid-withBorderColor` rule above adds
                    // a 1px borderRight to anything with that class. The footer
                    // spans the full grid width so its border doubles up with
                    // the grid's own right border — strip it here.
                    borderRight: 0
                },
                // The column-separator border on the LAST column doubles up
                // with the grid's own right edge — two hairlines a pixel apart,
                // most obvious on a grid inside a card. Same problem the footer
                // has above, so the same fix: strip that one border.
                //
                // The last header/cell isn't `:last-child` — MUI renders filler
                // elements after it (`filler--horizontal`, `scrollbarFiller`,
                // `cellEmpty`) — so it's identified by what follows it.
                '& .MuiDataGrid-columnHeader:has(+ .MuiDataGrid-filler), & .MuiDataGrid-columnHeader:has(+ .MuiDataGrid-scrollbarFiller)': {
                    borderRight: 0
                },
                '& .MuiDataGrid-cell:has(+ .MuiDataGrid-cellEmpty), & .MuiDataGrid-cell:has(+ .MuiDataGrid-filler), & .MuiDataGrid-cell:has(+ .MuiDataGrid-scrollbarFiller), & .MuiDataGrid-cellEmpty': {
                    borderRight: 0
                },
                // The filler row (rendered when the data doesn't fill the
                // viewport) uses the wrong default border color. Force it to
                // match our divider so the bottom edge of partially-filled
                // grids reads cleanly.
                '& .MuiDataGrid-filler': {
                    borderBottomColor: `${(theme.vars || theme).palette.divider} !important`
                },
                overflow: 'clip',
                borderColor: (theme.vars || theme).palette.divider,
                '&.MuiDataGrid-root': {
                    backgroundColor: (theme.vars || theme).palette.background.paper
                },
                '& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer': {
                    borderBottomWidth: 0
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                    borderRightWidth: 0,
                    borderBottomWidth: 0
                },
                ['& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader']: {
                    borderBottom: '1px solid',
                    backgroundColor: (theme.vars || theme).palette.background.paper,
                    borderBottomColor: `${(theme.vars || theme).palette.divider} !important`
                },
                [`& .${gridClasses.footerContainer}`]: {
                    backgroundColor: (theme.vars || theme).palette.background.paper
                },
                [`& .${checkboxClasses.root}`]: {
                    padding: theme.spacing(0.5),
                    '& > svg': {
                        fontSize: '1rem'
                    }
                },
                // Keep the selection column's checkboxes dead-centre (cells and
                // header) whatever width the column ends up with — the shrunken
                // checkbox above makes any drift obvious against the cell borders.
                '& .MuiDataGrid-cellCheckbox': {
                    justifyContent: 'center',
                    padding: 0
                },
                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                    justifyContent: 'center'
                },
                '& .MuiDataGrid-columnHeaderCheckbox': {
                    padding: 0
                },
                [`& .${tablePaginationClasses.root}`]: {
                    marginRight: theme.spacing(1),
                    '& .MuiIconButton-root': {
                        maxHeight: 32,
                        maxWidth: 32,
                        '& > svg': {
                            fontSize: '1rem'
                        }
                    }
                },
                '& .total-row': {
                    backgroundColor: (theme.vars || theme).palette.background.paper,
                    ':hover': {
                        backgroundColor: (theme.vars || theme).palette.background.paper
                    },
                    '& .MuiDataGrid-cell': {
                        fontWeight: 'bold'
                    }
                },
                '& .MuiDataGrid-row': {
                    position: 'relative'
                },
                '& .MuiDataGrid-row.success': {
                    backgroundColor: alpha(green[50], 0.3),
                    '&:hover': {
                        backgroundColor: alpha(green[100], 0.35)
                    },
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: '2px',
                        backgroundColor: alpha(green[400], 0.7)
                    },
                    '.MuiDataGrid-cell--pinnedLeft': {
                        borderLeft: `2px solid ${alpha(green[400], 0.7)}`
                    },
                    '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                        backgroundColor: alpha(green[100], 0.3),
                        '&:hover': {
                            backgroundColor: alpha(green[100], 0.55)
                        }
                    },
                    '&.MuiDataGrid-row.Mui-selected': {
                        backgroundColor: alpha(green[100], 0.5),
                        '&:hover': {
                            backgroundColor: alpha(green[100], 0.7)
                        },
                        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                            backgroundColor: alpha(green[100], 0.5),
                            '&:hover': {
                                backgroundColor: alpha(green[100], 0.7)
                            }
                        }
                    }
                },
                '& .MuiDataGrid-row.warning': {
                    backgroundColor: alpha(warningOrange.surface, 0.3),
                    '&:hover': {
                        backgroundColor: alpha(warningOrange.surface, 0.6)
                    },
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: '2px',
                        backgroundColor: alpha(warningOrange.fill, 0.7)
                    },
                    '.MuiDataGrid-cell--pinnedLeft': {
                        borderLeft: `2px solid ${alpha(warningOrange.fill, 0.7)}`
                    },
                    '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                        backgroundColor: alpha(warningOrange.surface, 0.5),
                        '&:hover': {
                            backgroundColor: alpha(warningOrange.surface, 0.9)
                        }
                    },
                    '&.MuiDataGrid-row.Mui-selected': {
                        backgroundColor: alpha(warningOrange.surface, 0.8),
                        '&:hover': {
                            backgroundColor: alpha(warningOrange.surface, 1)
                        },
                        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                            backgroundColor: alpha(warningOrange.tint, 0.4),
                            '&:hover': {
                                backgroundColor: alpha(warningOrange.tint, 0.7)
                            }
                        }
                    }
                },
                '& .MuiDataGrid-row.danger': {
                    backgroundColor: alpha(red[50], 0.3),
                    '&:hover': {
                        backgroundColor: alpha(red[50], 0.6)
                    },
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: '2px',
                        backgroundColor: alpha(red[400], 0.7)
                    },
                    '.MuiDataGrid-cell--pinnedLeft': {
                        borderLeft: `2px solid ${alpha(red[400], 0.7)}`
                    },
                    '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                        backgroundColor: alpha(red[50], 0.5),
                        '&:hover': {
                            backgroundColor: alpha(red[50], 0.9)
                        }
                    },
                    '&.MuiDataGrid-row.Mui-selected': {
                        backgroundColor: alpha(red[50], 0.8),
                        '&:hover': {
                            backgroundColor: alpha(red[50], 1)
                        },
                        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                            backgroundColor: alpha(red[100], 0.4),
                            '&:hover': {
                                backgroundColor: alpha(red[100], 0.7)
                            }
                        }
                    }
                },
                '& .MuiDataGrid-row.info': {
                    backgroundColor: alpha(blue[50], 0.3),
                    '&:hover': {
                        backgroundColor: alpha(blue[50], 0.6)
                    },
                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: '2px',
                        backgroundColor: alpha(blue[400], 0.7)
                    },
                    '.MuiDataGrid-cell--pinnedLeft': {
                        borderLeft: `2px solid ${alpha(blue[400], 0.7)}`
                    },
                    '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                        backgroundColor: alpha(blue[50], 0.5),
                        '&:hover': {
                            backgroundColor: alpha(blue[50], 0.9)
                        }
                    },
                    '&.MuiDataGrid-row.Mui-selected': {
                        backgroundColor: alpha(blue[50], 0.8),
                        '&:hover': {
                            backgroundColor: alpha(blue[50], 1)
                        },
                        '.MuiDataGrid-cell--pinnedLeft, .MuiDataGrid-cell--pinnedRight': {
                            backgroundColor: alpha(blue[100], 0.4),
                            '&:hover': {
                                backgroundColor: alpha(blue[100], 0.7)
                            }
                        }
                    }
                },
                '& .MuiDataGrid-row.dimmed': {
                    '& .MuiDataGrid-cell': {
                        opacity: 0.5
                    }
                }
            }),
            toolbar: ({theme}) => ({
                borderBottom: '1px solid',
                borderBottomColor: (theme.vars || theme).palette.divider
                // '& .MuiButton-root': {
                //     marginBottom: 'calc(0.5 * var(--template-spacing))'
                // }
            }),
            cell: ({theme}) => ({
                borderTopColor: (theme.vars || theme).palette.divider
            }),
            menu: ({theme}) => ({
                borderRadius: theme.shape.borderRadius,
                backgroundImage: 'none',
                [`& .${paperClasses.root}`]: {
                    border: `1px solid ${(theme.vars || theme).palette.divider}`
                },

                [`& .${menuItemClasses.root}`]: {
                    margin: '0 4px'
                },
                [`& .${listItemIconClasses.root}`]: {
                    marginRight: 0
                },
                [`& .${listClasses.root}`]: {
                    paddingLeft: 0,
                    paddingRight: 0
                }
            }),
            row: ({theme}) => ({
                cursor: 'pointer',
                // Plain (unclassed) rows only — status/tinted rows define their
                // own hovers below. The default action.hover read too dark on the
                // white background, so use a subtler neutral tint.
                '&:hover, &:hover .MuiDataGrid-cell--pinnedLeft': {
                    backgroundColor: alpha(gray[200], 0.12)
                },
                '&.Mui-selected': {
                    background: (theme.vars || theme).palette.action.selected,
                    '&:hover': {
                        backgroundColor: alpha(gray[200], 0.12)
                    }
                }
            }),
            menuIconButton: () => ({
                border: 'none',
                backgroundColor: 'transparent',
                '&:hover': {
                    backgroundColor: gray[100]
                },
                '&:active': {
                    backgroundColor: gray[200]
                }
            }),
            filterForm: ({theme}) => ({
                gap: theme.spacing(1),
                alignItems: 'flex-end'
            }),
            columnsManagementHeader: ({theme}) => ({
                paddingRight: theme.spacing(3),
                paddingLeft: theme.spacing(3)
            }),
            columnHeaderTitleContainer: {
                flexGrow: 1,
                justifyContent: 'space-between'
            },
            columnHeaderDraggableContainer: {paddingRight: 2}
        }
    }
};
